/**
 * Database error codes and their user-friendly message keys (for i18n)
 */
const ERROR_CODE_MESSAGE_KEYS: Record<string, string> = {
  "42501": "ui:errors.permission_denied",
  "23505": "ui:errors.duplicate_record",
  "23503": "ui:errors.integrity_constraint",
  "23502": "ui:errors.required_fields_missing",
  "42P01": "ui:errors.resource_not_found",
  "08006": "ui:errors.database_connection_failed",
  "08003": "ui:errors.connection_not_exist",
  "53300": "ui:errors.too_many_connections",
  "P0001": "ui:errors.business_rule_violated",
};

/**
 * HTTP status codes and their user-friendly message keys (for i18n)
 */
const HTTP_STATUS_MESSAGE_KEYS: Record<number, string> = {
  429: "ui:errors.rate_limited",
};

/**
 * Supabase Auth error codes and their user-friendly message keys (for i18n)
 * @see https://supabase.com/docs/reference/javascript/auth-error
 */
const AUTH_ERROR_MESSAGE_KEYS: Record<string, string> = {
  // Authentication errors
  "invalid_credentials": "auth:errors.invalid_credentials",
  "invalid_grant": "auth:errors.invalid_credentials",
  "user_not_found": "auth:errors.invalid_credentials",
  "email_not_confirmed": "auth:errors.email_not_confirmed",

  // Registration errors
  "user_already_registered": "auth:errors.user_already_exists",
  "email_exists": "auth:errors.user_already_exists",
  "signup_disabled": "auth:errors.signup_disabled",

  // Password errors
  "weak_password": "auth:errors.weak_password",
  "same_password": "auth:errors.same_password",

  // Token/session errors
  "invalid_token": "auth:errors.invalid_token",
  "expired_token": "auth:errors.expired_token",
  "token_expired": "auth:errors.expired_token",
  "refresh_token_not_found": "auth:errors.session_expired",
  "session_not_found": "auth:errors.session_expired",

  // Rate limiting
  "over_request_rate_limit": "auth:errors.rate_limit_exceeded",
  "over_email_send_rate_limit": "auth:errors.email_rate_limit",

  // Email errors
  "email_provider_disabled": "auth:errors.email_service_unavailable",
  "email_not_sent": "auth:errors.email_not_sent",

  // OAuth errors
  "oauth_provider_error": "auth:errors.oauth_failed",
  "provider_email_needs_verification": "auth:errors.email_not_confirmed",
};

/**
 * Specific constraint violation message keys for better UX
 *
 * Note: Foreign key constraint violations (23503) that reference appointments
 * with treatments will be handled by a special context check in CalendarEventHandlers
 * using the scheduling:errors.delete_blocked_by_treatments key
 */
const CONSTRAINT_ERROR_MESSAGE_KEYS: Record<string, string> = {
  "unq_active_relation": "patients:messages.errors.relationship_already_exists",
  "unq_patients_email": "patients:messages.errors.email_already_exists",
  "unique_patient_tooth_condition": "ui:errors.tooth_condition_exists",
  "patient_relationships_pkey": "patients:messages.errors.relationship_record_exists",
  "patient_responsibilities_pkey": "patients:messages.errors.responsibility_already_exists",
};

/**
 * User-actionable error codes returned by the appointment-portal edge function.
 * Only codes the patient can act on are mapped here; technical codes
 * (e.g. invalid_request_body) deliberately fall through to the generic message
 * so they stay log/Sentry-only.
 */
const PORTAL_ERROR_MESSAGE_KEYS: Record<string, string> = {
  "reschedule_not_allowed": "patient-portal:errors.reschedule_not_allowed",
  "cancel_not_allowed": "patient-portal:errors.cancel_not_allowed",
  "slot_unavailable": "patient-portal:errors.slot_unavailable",
};

/**
 * Custom SQLSTATEs raised by public_book_appointment. The patient can act on
 * both (pick another time or service), so they are expected outcomes rather
 * than faults.
 */
const BOOKING_ERROR_MESSAGE_KEYS: Record<string, string> = {
  "ZB001": "online-bookings:errors.slot_unavailable",
  "ZB002": "online-bookings:errors.procedure_not_bookable",
};

/**
 * Custom SQLSTATEs raised by set_invoice_installments and the invoice total
 * lock. All are outcomes staff can act on (remove the plan, fix the schedule).
 */
const INSTALLMENT_ERROR_MESSAGE_KEYS: Record<string, string> = {
  "ZI001": "invoicing:installments.errors.total_locked",
  "ZI002": "invoicing:installments.errors.not_allowed",
  "ZI003": "invoicing:installments.errors.invalid_schedule",
};

/**
 * User-actionable error codes derived from the invite-user edge function.
 * The hook (useInviteTeamMember) maps the function's 403 responses to these
 * stable codes; a global "403 → session" rule is avoided because 403 semantics
 * differ per endpoint.
 */
const INVITE_ERROR_MESSAGE_KEYS: Record<string, string> = {
  "invite_session_stale": "team:invite.errors.session_stale",
  "invite_not_admin": "team:invite.errors.not_admin",
};

/**
 * Message key for a user-actionable error `code`, from whichever map owns it.
 * Checked on Error instances as well as plain objects: edge-function and invite
 * errors arrive as `Error`s carrying a `code` (see unwrapFunctionsError), while
 * PostgREST errors arrive as plain `{ code, message }` objects.
 */
function getActionableCodeMessageKey(
  error: unknown,
): { key: string; fallback: string } | null {
  if (typeof error !== "object" || error === null) return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (typeof code !== "string") return null;

  // [map, default fallback, whether the server's message is user-facing prose].
  // Booking and installment errors carry the bare code as their message, so it is not used.
  const maps: Array<[Record<string, string>, string, boolean]> = [
    [PORTAL_ERROR_MESSAGE_KEYS, "This action is no longer available.", true],
    [INVITE_ERROR_MESSAGE_KEYS, "The invitation could not be sent.", true],
    [BOOKING_ERROR_MESSAGE_KEYS, "This booking could not be completed. Please choose another time.", false],
    [INSTALLMENT_ERROR_MESSAGE_KEYS, "The installment plan could not be saved.", false],
  ];
  for (const [map, defaultFallback, useServerMessage] of maps) {
    const key = map[code];
    if (key) {
      const fallback =
        useServerMessage && typeof message === "string" && message ? message : defaultFallback;
      return { key, fallback };
    }
  }
  return null;
}

/**
 * Matches the NormalizedSupabaseError shape produced by the query/mutation
 * wrappers (structural, so this module does not import from shared/api).
 */
function isNormalizedError(
  error: unknown,
): error is Error & { translationKey: string; fallback: string } {
  return (
    error instanceof Error &&
    typeof (error as { translationKey?: unknown }).translationKey === "string" &&
    typeof (error as { fallback?: unknown }).fallback === "string"
  );
}

/**
 * Extracts constraint name from PostgreSQL error message
 */
function extractConstraintName(error: unknown): string | null {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;
    const message = errorObj.message || errorObj.details || "";
    
    // Look for constraint names in typical PostgreSQL error formats
    const constraintMatch = message.match(/violates unique constraint "([^"]+)"/i) ||
                           message.match(/constraint "([^"]+)" violated/i) ||
                           message.match(/violates.*constraint.*"([^"]+)"/i);
    
    if (constraintMatch && constraintMatch[1]) {
      return constraintMatch[1];
    }
  }
  return null;
}

/**
 * Gets the appropriate error message key for translation
 */
export function getErrorMessageKey(error: unknown): { key: string; fallback: string } {
  if (!error) {
    return { key: "ui:errors.unknown_error", fallback: "An unknown error occurred." };
  }

  // Already normalized by useSupabaseQuery/useSupabaseMutation: keep the key
  // resolved from the original error. Re-deriving it here would only see a
  // plain Error carrying translated text and degrade to the generic message.
  if (isNormalizedError(error)) {
    return { key: error.translationKey, fallback: error.fallback };
  }

  // Transient browser-level connectivity faults ("Load failed", "Failed to
  // fetch", offline, aborted requests) are not application errors. Give them a
  // friendly, actionable message instead of leaking the raw browser text.
  // Scoped to fetch-shaped failures on purpose: PostgreSQL connection codes
  // (08006/08003/53300) keep their more specific dedicated keys below.
  if (isBrowserFetchError(error)) {
    return {
      key: "ui:errors.network_error",
      fallback: "Please check your internet connection and try again.",
    };
  }

  // Handle string errors
  if (typeof error === "string") {
    if (error.toLowerCase().includes("violates row-level security policy")) {
      return { key: "ui:errors.permission_denied_rls", fallback: "You don't have permission to perform this action. Please contact your administrator." };
    }
    return { key: "ui:errors.generic", fallback: error };
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    if (error.message.toLowerCase().includes("violates row-level security policy")) {
      return { key: "ui:errors.permission_denied_rls", fallback: "You don't have permission to perform this action. Please contact your administrator." };
    }

    // Check for rate-limit/duplicate request errors (e.g., InvoicePdfApiError)
    const errorWithStatus = error as Error & { statusCode?: number };
    if (errorWithStatus.statusCode === 429 || error.message.toLowerCase().includes("duplicate request")) {
      return { key: "ui:errors.rate_limited", fallback: "Please wait before trying again." };
    }

    const actionable = getActionableCodeMessageKey(error);
    if (actionable) return actionable;

    return { key: "ui:errors.generic", fallback: error.message };
  }
  
  // Handle Supabase/PostgreSQL error objects
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;

    // Check for RLS policy violations
    const checkRLSViolation = (text: string): boolean => {
      return text.toLowerCase().includes("violates row-level security policy");
    };

    // Check for custom security violations (from RAISE EXCEPTION in RPC functions)
    const checkSecurityViolation = (text: string): boolean => {
      return text.toLowerCase().includes("security violation");
    };

    if (errorObj.message && typeof errorObj.message === "string" && checkRLSViolation(errorObj.message)) {
      return { key: "ui:errors.permission_denied_rls", fallback: "You don't have permission to perform this action. Please contact your administrator." };
    }

    if (errorObj.details && typeof errorObj.details === "string" && checkRLSViolation(errorObj.details)) {
      return { key: "ui:errors.permission_denied_rls", fallback: "You don't have permission to perform this action. Please contact your administrator." };
    }

    // Check for custom security violations in message
    if (errorObj.message && typeof errorObj.message === "string" && checkSecurityViolation(errorObj.message)) {
      return { key: "ui:errors.permission_denied", fallback: "You don't have permission to perform this action." };
    }

    // Check for auth-specific error codes (Supabase auth errors)
    // These can be in error.code, error.__isAuthError + error.message, or embedded in the message
    if (errorObj.__isAuthError || errorObj.name === "AuthError" || errorObj.name === "AuthApiError") {
      // Try to extract auth error code from various locations
      const authCode = errorObj.code || errorObj.error_code || errorObj.status;

      if (authCode && AUTH_ERROR_MESSAGE_KEYS[authCode]) {
        const fallback = errorObj.message || `Authentication error: ${authCode}`;
        return { key: AUTH_ERROR_MESSAGE_KEYS[authCode], fallback };
      }

      // Check message for known auth error patterns
      const message = (errorObj.message || "").toLowerCase();
      for (const [code, key] of Object.entries(AUTH_ERROR_MESSAGE_KEYS)) {
        if (message.includes(code.replace(/_/g, " ")) || message.includes(code)) {
          return { key, fallback: errorObj.message || `Authentication error` };
        }
      }
    }

    // Check for HTTP status code-based errors (e.g., rate limiting)
    const httpStatus = errorObj.statusCode || errorObj.status;
    if (typeof httpStatus === "number" && HTTP_STATUS_MESSAGE_KEYS[httpStatus]) {
      return {
        key: HTTP_STATUS_MESSAGE_KEYS[httpStatus],
        fallback: errorObj.message || `Request failed (${httpStatus})`,
      };
    }

    // Check for rate-limit/duplicate request patterns in message
    if (errorObj.message && typeof errorObj.message === "string") {
      if (errorObj.message.toLowerCase().includes("duplicate request")) {
        return {
          key: "ui:errors.rate_limited",
          fallback: "Please wait before trying again.",
        };
      }
    }

    // Check for specific constraint violations
    const constraintName = extractConstraintName(error);
    if (constraintName && CONSTRAINT_ERROR_MESSAGE_KEYS[constraintName]) {
      return {
        key: CONSTRAINT_ERROR_MESSAGE_KEYS[constraintName],
        fallback: `Constraint violation: ${constraintName}`
      };
    }

    // Check for user-actionable portal, invite and booking error codes
    const actionable = getActionableCodeMessageKey(error);
    if (actionable) return actionable;

    // Check for general database error codes
    if (errorObj.code && typeof errorObj.code === "string") {
      const messageKey = ERROR_CODE_MESSAGE_KEYS[errorObj.code];
      if (messageKey) {
        // Use the actual error message as fallback if available, otherwise use generic message
        const fallback = errorObj.message && typeof errorObj.message === "string"
          ? errorObj.message
          : `Database error (${errorObj.code})`;
        return { key: messageKey, fallback };
      }
    }

    // Fallback to raw message
    if (errorObj.message && typeof errorObj.message === "string") {
      return { key: "ui:errors.generic", fallback: errorObj.message };
    }

    if (errorObj.details && typeof errorObj.details === "string") {
      return { key: "ui:errors.generic", fallback: errorObj.details };
    }
  }
  
  return { key: "ui:errors.unexpected", fallback: "An unexpected error occurred. Please try again." };
}

/**
 * Legacy function for backward compatibility - converts database error codes to user-friendly messages
 * @deprecated Use getErrorMessageKey instead for i18n support
 */
export function getErrorMessage(error: unknown): string {
  const { fallback } = getErrorMessageKey(error);
  return fallback;
}

/**
 * Checks if an error is a permission/RLS error
 */
function isPermissionError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;
    return errorObj.code === "42501";
  }
  return false;
}

/**
 * Checks if an error is a validation/constraint error
 */
function isValidationError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;
    return ["23505", "23503", "23502"].includes(errorObj.code);
  }
  return false;
}

/**
 * Fetch-shaped failure messages the browser produces when it couldn't complete
 * a request (offline, dropped connection, aborted, CORS). Safari/WebKit uses
 * "Load failed"; Chromium/Firefox use "Failed to fetch" / "NetworkError when
 * attempting to fetch resource" / "Network request failed".
 */
const NETWORK_FAILURE_MESSAGE_HINTS = [
  "load failed",
  "failed to fetch",
  "networkerror",
  "network request failed",
];

/**
 * Checks for a browser fetch-layer failure specifically — the `TypeError:
 * Load failed` / `Failed to fetch` class of transient connectivity faults.
 *
 * A bare `TypeError` is too broad to treat as a network fault on its own (real
 * programming TypeErrors must still reach Sentry), so a fetch-shaped message is
 * required. `NetworkError` as an error name is unambiguous.
 */
function isBrowserFetchError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const errorObj = error as any;
  const message =
    typeof errorObj.message === "string" ? errorObj.message.toLowerCase() : "";

  if (errorObj.name === "NetworkError") return true;
  if (
    (errorObj.name === "TypeError" || !errorObj.name) &&
    NETWORK_FAILURE_MESSAGE_HINTS.some((hint) => message.includes(hint))
  ) {
    return true;
  }
  return false;
}

/**
 * Checks if an error is a network/connection error (transient connectivity
 * fault rather than an application bug). Exported so Sentry capture sites can
 * suppress these — they are noise, not faults. Broader than
 * {@link isBrowserFetchError}: also covers PostgreSQL connection-drop codes and
 * generic network keywords.
 */
export function isNetworkError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;

    // Database connection errors (transient server-side connectivity)
    if (["08006", "08003", "53300"].includes(errorObj.code)) {
      return true;
    }

    // Network error messages
    if (typeof errorObj.message === "string") {
      const message = errorObj.message.toLowerCase();
      if (
        message.includes("network") ||
        message.includes("connection") ||
        message.includes("fetch") ||
        message.includes("timeout") ||
        message.includes("offline")
      ) {
        return true;
      }
    }

    // Browser fetch-layer failures ("Load failed", etc.)
    if (isBrowserFetchError(error)) {
      return true;
    }
  }

  return false;
}

/**
 * True when the error is an expected, user-actionable business-rule rejection
 * (e.g. a portal 403 the patient can act on) rather than an application fault.
 * Exported so Sentry capture sites can suppress these — like {@link isNetworkError},
 * they are expected outcomes, not bugs. Keyed off the same user-actionable code
 * maps {@link getErrorMessageKey} uses, so the two never drift.
 */
export function isExpectedBusinessError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") {
      return (
        code in PORTAL_ERROR_MESSAGE_KEYS ||
        code in INVITE_ERROR_MESSAGE_KEYS ||
        code in BOOKING_ERROR_MESSAGE_KEYS ||
        code in INSTALLMENT_ERROR_MESSAGE_KEYS
      );
    }
  }
  return false;
}

/**
 * Checks if an error is a module loading error (chunk loading failure)
 */
function isModuleLoadingError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;

    if (typeof errorObj.message === "string") {
      const message = errorObj.message.toLowerCase();
      return message.includes("loading chunk") ||
             message.includes("importing a module") ||
             message.includes("dynamically imported module") ||
             message.includes("failed to import");
    }
  }

  return false;
}

/**
 * Checks if an error is a token/session error (expired, invalid, etc.)
 */
export function isTokenError(error: unknown): boolean {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;

    // Check for specific auth error codes
    if (errorObj.code && typeof errorObj.code === "string") {
      const tokenErrorCodes = ["invalid_token", "expired_token", "token_expired", "refresh_token_not_found", "session_not_found"];
      if (tokenErrorCodes.includes(errorObj.code)) {
        return true;
      }
    }

    // Fallback: check message content
    if (typeof errorObj.message === "string") {
      const message = errorObj.message.toLowerCase();
      return message.includes("token") || message.includes("expired") || message.includes("session");
    }
  }

  return false;
}

/**
 * Gets a user-friendly error message with appropriate fallback based on error type
 */
export function getUserFriendlyErrorMessage(error: unknown, isDevelopment = false): string {
  if (isNetworkError(error)) {
    return isDevelopment 
      ? `Network error: ${getErrorMessage(error)}`
      : "Please check your internet connection and try again.";
  }
  
  if (isModuleLoadingError(error)) {
    return isDevelopment
      ? `Module loading error: ${getErrorMessage(error)}`
      : "Failed to load the application. Please refresh the page.";
  }
  
  if (isPermissionError(error)) {
    return "You don't have permission to perform this action.";
  }
  
  if (isValidationError(error)) {
    return getErrorMessage(error);
  }
  
  // Default fallback
  const baseMessage = getErrorMessage(error);
  if (isDevelopment) {
    return baseMessage;
  }
  
  // In production, be more generic for unknown errors
  return "Something went wrong. Please try again or contact support if the problem persists.";
}